import type { LandingComponents } from "@/puck/blocks/blockTypes";
import { buildBusyworkSectionProps } from "@/puck/defaults/busyworkSectionDefaults";
import { buildFilledWhileYouWatchSectionProps } from "@/puck/defaults/filledWhileYouWatchSectionDefaults";
import { buildUploadExpertsSectionProps } from "@/puck/defaults/uploadExpertsSectionDefaults";
import type { ColumnsBlockProps } from "@/puck/types";
import {
  clampColumnCount,
  normalizeColumnBorderStyle,
  normalizeColumnVerticalAlign,
} from "@/utils/columnStyles";
import {
  normalizeCardRadius,
  normalizeSectionBackground,
} from "@/utils/cardStyles";
import { createColumnsBlockConfig } from "@/puck/blocks/shared/columnsBlockConfig";
import { regeneratePuckBlockIds } from "@/utils/studioTemplates";

type TemplateColumnsProps = ColumnsBlockProps & { id: string };

const busyworkColumnsConfig = createColumnsBlockConfig({
  blockName: "TemplateBusywork",
  label: "Busywork section",
  variant: "landing",
  withHeader: true,
  defaultColumnCount: 3,
  headerDefaults: {
    eyebrow: "DOES THE WORK, NOT JUST THE TALKING",
    title: "The busywork your team dreads, <em>just done.</em>",
    subtitle:
      "Access Gail's conversational AI agents and the everyday insurance work runs itself, start to finish.",
    headerAlign: "center",
  },
});

const uploadExpertsColumnsConfig = createColumnsBlockConfig({
  blockName: "TemplateUploadExperts",
  label: "Upload experts section",
  variant: "landing",
  withHeader: true,
  defaultColumnCount: 2,
  headerDefaults: {
    eyebrow: "YOUR KNOWLEDGE, YOUR AI",
    title: "Build your own AI experts. <em>Just upload your documents.</em>",
    subtitle:
      "Drop in your guidelines, carrier appetites, SOPs, and forms. Gail turns them into an expert your whole team can ask.",
    headerAlign: "left",
  },
});

const filledWhileYouWatchColumnsConfig = createColumnsBlockConfig({
  blockName: "TemplateFilledWhileYouWatch",
  label: "Filled while you watch section",
  variant: "landing",
  withHeader: false,
  defaultColumnCount: 2,
});

function getTemplateColumnPatch(
  props: TemplateColumnsProps,
  defaultColumnCount: number,
) {
  const columnCount = clampColumnCount(
    Number(props.columnCount) || defaultColumnCount,
  );
  const showBorder = normalizeColumnBorderStyle(props.showBorder);
  const columnVerticalAlign = normalizeColumnVerticalAlign(
    props.columnVerticalAlign,
  );
  const columnBackground = normalizeSectionBackground(props.columnBackground);
  const columnRadius = normalizeCardRadius(props.columnRadius);
  const patch: Partial<TemplateColumnsProps> = {};

  if (columnCount !== props.columnCount) {
    patch.columnCount = columnCount;
  }

  if (showBorder !== props.showBorder) {
    patch.showBorder = showBorder;
  }

  if (columnVerticalAlign !== props.columnVerticalAlign) {
    patch.columnVerticalAlign = columnVerticalAlign;
  }

  if (columnBackground !== props.columnBackground) {
    patch.columnBackground = columnBackground;
  }

  if (columnRadius !== props.columnRadius) {
    patch.columnRadius = columnRadius;
  }

  return patch;
}

type TemplateComponentType =
  | "TemplateBusywork"
  | "TemplateUploadExperts"
  | "TemplateFilledWhileYouWatch";

function createTemplateResolveData(
  componentType: TemplateComponentType,
  placeholderId: string,
  defaultColumnCount: number,
) {
  return (data: { props: TemplateColumnsProps }, { trigger }: { trigger: string }) => {
    const props: TemplateColumnsProps = {
      ...data.props,
      id:
        typeof data.props?.id === "string" ? data.props.id : placeholderId,
      ...getTemplateColumnPatch(data.props, defaultColumnCount),
    };

    if (trigger === "insert") {
      return {
        props: regeneratePuckBlockIds(
          {
            type: componentType,
            props,
          },
          { preserveRootId: true },
        ).props,
      };
    }

    const patch = getTemplateColumnPatch(data.props, defaultColumnCount);
    return Object.keys(patch).length > 0 ? { props: patch } : { props: {} };
  };
}

function buildUploadExpertsTemplateDefaultProps() {
  return regeneratePuckBlockIds(
    {
      type: "TemplateUploadExperts",
      props: buildUploadExpertsSectionProps("template-upload-experts-placeholder"),
    },
    { preserveRootId: true },
  ).props;
}

function buildFilledWhileYouWatchTemplateDefaultProps() {
  return regeneratePuckBlockIds(
    {
      type: "TemplateFilledWhileYouWatch",
      props: buildFilledWhileYouWatchSectionProps(
        "template-filled-while-you-watch-placeholder",
      ),
    },
    { preserveRootId: true },
  ).props;
}

export const templateConfigs: Pick<
  LandingComponents,
  "TemplateBusywork" | "TemplateUploadExperts" | "TemplateFilledWhileYouWatch"
> = {
  TemplateBusywork: {
    ...busyworkColumnsConfig,
    defaultProps: buildBusyworkSectionProps("template-busywork-placeholder"),
    resolveData: createTemplateResolveData(
      "TemplateBusywork",
      "template-busywork-placeholder",
      3,
    ),
  } as Pick<LandingComponents, "TemplateBusywork">["TemplateBusywork"],
  TemplateUploadExperts: {
    ...uploadExpertsColumnsConfig,
    defaultProps: buildUploadExpertsTemplateDefaultProps(),
    resolveData: createTemplateResolveData(
      "TemplateUploadExperts",
      "template-upload-experts-placeholder",
      2,
    ),
  } as Pick<LandingComponents, "TemplateUploadExperts">["TemplateUploadExperts"],
  TemplateFilledWhileYouWatch: {
    ...filledWhileYouWatchColumnsConfig,
    defaultProps: buildFilledWhileYouWatchTemplateDefaultProps(),
    resolveData: createTemplateResolveData(
      "TemplateFilledWhileYouWatch",
      "template-filled-while-you-watch-placeholder",
      2,
    ),
  } as Pick<
    LandingComponents,
    "TemplateFilledWhileYouWatch"
  >["TemplateFilledWhileYouWatch"],
};
